import React, { useEffect, useMemo, useState } from "react";
import { tokens } from "@/tokens";
import {
  Box,
  Stack,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  Skeleton,
  Divider,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  OpenInNew as LinkIcon,
  ExpandMore,
  Star as StarIcon,
} from "@mui/icons-material";
import { tariff_code_placeholder } from "@/constants";
import DatePicker from "@/components/date-picker";
import { useCalculateDutiesQuery, DutyCalculationInput, LineType, deriveMetalTypeEntries } from "@/services/product-catalogue";
import CreateProductForm from "../create-product-form";
import { useGetCountrySummaryQuery, useGetHTSCodesQuery } from "@/services/dashboard/sm-api";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { CountrySelect, HTSCodeSelect } from "../product-select-fields";
import { useLazyCheckMetalPercentageRequiredQuery } from "@/services/product-catalogue/metal-percentage-api";
import { getAuthorityStyle } from "../rule-form-section/helpers";
import { useGetDutyColumnsQuery, DutyColumn } from "@/services/product-catalogue/duty-columns-api";

const DutyCalculator: React.FC = () => {
  const { user } = useAppSelector((state: any) => state.auth);
  const { selectedClient } = useAppSelector((state: any) => state.client);
  const { selectedTenant } = useAppSelector((state: any) => state.tenantManagement);

  const [htsCode, setHtsCode] = useState("");
  const [countryOfOrigin, setCountryOfOrigin] = useState("");
  const [metalPercentage, setMetalPercentage] = useState<string>("");
  const [showMetalPercentage, setShowMetalPercentage] = useState(false);
  // Per-metal-type breakdown state (used when multiple metal rules apply)
  const [metalBreakdownState, setMetalBreakdownState] = useState<Record<string, string>>({});
  const [effectiveDate, setEffectiveDate] = useState<string>(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  });

  // Debounced input values for live compute
  const [debouncedHtsCode, setDebouncedHtsCode] = useState("");
  const [debouncedCountryOfOrigin, setDebouncedCountryOfOrigin] = useState("");
  const [debouncedMetalPercentage, setDebouncedMetalPercentage] = useState<string>("");
  const [debouncedMetalBreakdown, setDebouncedMetalBreakdown] = useState<Record<string, string>>({});

  useEffect(() => {
    const t = setTimeout(() => setDebouncedHtsCode(htsCode), 400);
    return () => clearTimeout(t);
  }, [htsCode]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedCountryOfOrigin(countryOfOrigin), 400);
    return () => clearTimeout(t);
  }, [countryOfOrigin]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedMetalPercentage(metalPercentage), 400);
    return () => clearTimeout(t);
  }, [metalPercentage]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedMetalBreakdown(metalBreakdownState), 400);
    return () => clearTimeout(t);
  }, [metalBreakdownState]);

  // Metal percentage check — must be declared before input memo
  const [checkMetalPercentage, { data: metalReqData }] = useLazyCheckMetalPercentageRequiredQuery();

  // Detect applicable metal rules and multi-metal mode
  const applicableMetalRules = metalReqData?.applicableMetalRules || [];
  const isMultiMetal = applicableMetalRules.length > 1;

  const metalTypeEntries = useMemo(() => deriveMetalTypeEntries(applicableMetalRules), [applicableMetalRules]);

  const input: DutyCalculationInput | null = useMemo(() => {
    if (!debouncedHtsCode || !debouncedCountryOfOrigin) return null;

    // Multi-metal mode: pack per-metal percentages into metalBreakdown
    if (isMultiMetal && Object.keys(debouncedMetalBreakdown).length > 0) {
      const breakdown: Record<string, number> = {};
      Object.entries(debouncedMetalBreakdown).forEach(([key, val]) => {
        const n = Number(val);
        if (!isNaN(n) && n > 0) breakdown[key] = n;
      });
      if (Object.keys(breakdown).length > 0) {
        return {
          htsCode: debouncedHtsCode,
          countryOfOrigin: debouncedCountryOfOrigin,
          effectiveDate,
          metalBreakdown: breakdown as any,
        };
      }
    }

    return {
      htsCode: debouncedHtsCode,
      countryOfOrigin: debouncedCountryOfOrigin,
      effectiveDate,
      metalPercentage: debouncedMetalPercentage ? Number(debouncedMetalPercentage) : undefined,
    };
  }, [debouncedHtsCode, debouncedCountryOfOrigin, effectiveDate, debouncedMetalPercentage, isMultiMetal, debouncedMetalBreakdown]);

  const { data, isFetching } = useCalculateDutiesQuery(input!, { skip: !input });

  const totalRatePct = useMemo(() => ((data?.totalDutyRate ?? 0) * 100).toFixed(2), [data]);

  // Value form state
  const [goodsValue, setGoodsValue] = useState<string>("");

  // Create Product modal state
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const matchedLabel = useMemo(() => {
    if (!data?.htsMatchedCode) return null;
    const level = data?.htsMatchLevel ? `${data.htsMatchLevel}-digit` : 'code';
    return `Matched at ${level}: ${data.htsMatchedCode}`;
  }, [data]);

  const parsedGoodsValue = useMemo(() => {
    const n = Number(goodsValue.replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : n;
  }, [goodsValue]);

  const estimatedDutyAmount = useMemo(() => {
    const rate = data?.totalDutyRate ?? 0;
    return parsedGoodsValue * rate;
  }, [parsedGoodsValue, data]);

  // client selection logic mirrors product forms
  const isTDAdminTCD = useMemo(() => user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT", [user, selectedTenant]);
  const clientId = useMemo(() => {
    if (isTDAdminTCD) return selectedTenant?._id;
    if (selectedClient?._id) return selectedClient._id;
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) return user?._id;
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  // countries
  const { data: countrySummary, isLoading: isLoadingCountries } = useGetCountrySummaryQuery(
    { clientId: clientId || "" },
    { skip: !clientId }
  );
  const allCountryCodes = useMemo(() => countrySummary?.getCountryOriginSummary?.map((c: any) => c.country_code) || [], [countrySummary]);

  // hts options filtered by country (or all)
  const { data: htsCodes, isLoading: isLoadingHTS } = useGetHTSCodesQuery(
    { clientId: clientId || "", country_codes: countryOfOrigin ? [countryOfOrigin] : allCountryCodes },
    { skip: !clientId || allCountryCodes.length === 0 }
  );

  useEffect(() => {
    if (htsCode && countryOfOrigin) {
      const timer = setTimeout(() => {
        checkMetalPercentage({ htsCode: htsCode.replace(/\./g, ''), countryOfOrigin });
      }, 400);
      return () => clearTimeout(timer);
    } else {
      setShowMetalPercentage(false);
    }
  }, [htsCode, countryOfOrigin, checkMetalPercentage]);

  // Handle metal check response
  useEffect(() => {
    const required = Boolean(metalReqData?.isMetalPercentageRequired);
    setShowMetalPercentage(required);
    if (required && !isMultiMetal && !metalPercentage) {
      setMetalPercentage("100");
    }
    if (required && isMultiMetal) {
      // Initialize per-metal breakdown with defaults if empty
      setMetalBreakdownState((prev) => {
        const next = { ...prev };
        metalTypeEntries.forEach(({ key }) => {
          if (!next[key]) next[key] = "50";
        });
        return next;
      });
    }
  }, [metalReqData]);

  // When metal is not required anymore, clear any metal percentage
  useEffect(() => {
    if (!showMetalPercentage && metalPercentage) {
      setMetalPercentage("");
    }
    if (!showMetalPercentage) {
      setMetalBreakdownState({});
    }
  }, [showMetalPercentage]);

  // Fetch duty columns to build citations footnotes
  const { data: dutyColumnsResp } = useGetDutyColumnsQuery({ pageNo: 1, pageSize: 500 } as any);
  const dutyColumns: DutyColumn[] = dutyColumnsResp?.data || [];
  const columnMap = useMemo(() => {
    const m = new Map<string, DutyColumn>();
    dutyColumns.forEach(dc => dc && dc._id && m.set(dc._id, dc));
    return m;
  }, [dutyColumns]);
  const footnoteColumns = useMemo(() => {
    const ids = new Set<string>();
    (data?.breakdownLines || []).forEach(line => {
      (line.calculationBreakdown || []).forEach(calc => {
        if (calc.applied && typeof calc.columnId === 'string' && calc.columnId !== 'general-duty' && calc.columnId !== 'secondary-duty') {
          ids.add(calc.columnId as string);
        }
      });
    });
    return Array.from(ids)
      .map(id => columnMap.get(id))
      .filter((dc): dc is DutyColumn => !!dc && Array.isArray((dc as any).citations) && (dc as any).citations.length > 0);
  }, [data, columnMap]);

  const getLineColor = (lineType: string) => {
    if (lineType === LineType.METAL) return { bg: tokens.chip.amber.bg, text: tokens.chip.amber.text, border: tokens.chip.amber.bg };
    if (lineType === LineType.STEEL) return { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' };
    if (lineType === LineType.ALUMINUM) return { bg: '#E0E7FF', text: '#3730A3', border: '#C7D2FE' };
    if (lineType === LineType.COPPER) return { bg: '#FFE4E6', text: '#9F1239', border: '#FECDD3' };
    if (lineType === LineType.NONMETAL) return { bg: tokens.chip.blue.bg, text: tokens.chip.blue.text, border: tokens.chip.blue.bg };
    return { bg: tokens.chip.green.bg, text: tokens.chip.green.text, border: tokens.chip.green.bg };
  };

  const getLineLabel = (lineType: string) => {
    if (lineType === LineType.METAL) return 'Metal Content';
    if (lineType === LineType.STEEL) return 'Steel (232)';
    if (lineType === LineType.ALUMINUM) return 'Aluminum (232)';
    if (lineType === LineType.COPPER) return 'Copper (232)';
    if (lineType === LineType.NONMETAL) return 'Non-Metal Content';
    return 'Full Value';
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Box
        sx={{
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.border.container}`,
          bgcolor: tokens.bg.surface,
          boxShadow: tokens.shadow.sm,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          height: { md: 'calc(100vh - 160px)' },
          overflow: 'hidden',
        }}
      >
        {/* Left Panel — Form */}
        <Box
          sx={{
            width: { xs: '100%', md: 380 },
            flexShrink: 0,
            borderRight: { md: `1px solid ${tokens.border.default}` },
            borderBottom: { xs: `1px solid ${tokens.border.default}`, md: 'none' },
            p: 2.5,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.25 }}>
            Duty Calculator
          </Typography>
          <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mb: 2 }}>
            Calculate duties by HTS code, origin, and date.
          </Typography>

          <Stack spacing={2}>
            <CountrySelect
              value={countryOfOrigin}
              onChange={setCountryOfOrigin}
              countries={countrySummary?.getCountryOriginSummary || []}
              isLoading={isLoadingCountries}
              required
              label="Country of Origin"
            />
            <HTSCodeSelect
              value={htsCode}
              onChange={setHtsCode}
              htsCodes={htsCodes?.getHTSCodesByCountry || []}
              isLoading={isLoadingHTS}
              required
              label="HTS Code"
            />
            {showMetalPercentage && !isMultiMetal && (
              <>
                <TextField
                  label="Metal Percentage"
                  type="number"
                  size="small"
                  value={metalPercentage}
                  onChange={(e) => setMetalPercentage(e.target.value)}
                  placeholder="e.g., 85.5"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    inputProps: { min: 0, max: 100, step: 0.1 },
                  }}
                />
                {metalPercentage && Number(metalPercentage) < 100 && (
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, mt: -1 }}>
                    Non-Metal: {(100 - Number(metalPercentage)).toFixed(1)}%
                  </Typography>
                )}
              </>
            )}
            {showMetalPercentage && isMultiMetal && (
              <>
                {metalTypeEntries.map(({ key, label }) => (
                  <TextField
                    key={key}
                    label={label}
                    type="number"
                    size="small"
                    value={metalBreakdownState[key] || ""}
                    onChange={(e) => setMetalBreakdownState((prev) => ({ ...prev, [key]: e.target.value }))}
                    placeholder="e.g., 50"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      inputProps: { min: 0, max: 100, step: 0.1 },
                    }}
                  />
                ))}
                <Box sx={{ bgcolor: tokens.bg.muted, borderRadius: tokens.radius.sm, px: 1.5, py: 1, mt: -0.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, fontWeight: tokens.fontWeight.semibold }}>
                      Non-Metal Portion
                    </Typography>
                    <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
                      {Math.max(0, 100 - Object.values(metalBreakdownState).reduce((sum, v) => sum + (Number(v) || 0), 0)).toFixed(1)}%
                    </Typography>
                  </Stack>
                  {Object.values(metalBreakdownState).reduce((sum, v) => sum + (Number(v) || 0), 0) > 100 && (
                    <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.status.danger, mt: 0.25 }}>
                      Total metal percentages exceed 100%
                    </Typography>
                  )}
                </Box>
              </>
            )}
            <DatePicker
              value={effectiveDate}
              onChangeValue={(d) => setEffectiveDate(d ?? effectiveDate)}
            />
            <Button
              variant="contained"
              fullWidth
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setIsCreateOpen(true)}
              disabled={!htsCode || !countryOfOrigin}
              sx={{
                fontWeight: tokens.fontWeight.bold,
                fontSize: tokens.fontSize.sm2,
                borderRadius: tokens.radius.md,
                py: 1,
                bgcolor: tokens.brand.primary,
                '&:hover': { bgcolor: tokens.brand.primaryHover },
              }}
            >
              Add Product
            </Button>
          </Stack>
        </Box>

        {/* Right Panel — Results */}
        <Box
          sx={{
            flex: 1,
            p: 2.5,
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          {/* Empty state */}
          {!isFetching && !data && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 300 }}>
              <Stack alignItems="center" spacing={2} sx={{ maxWidth: 320, textAlign: 'center' }}>
                <img src={tariff_code_placeholder} alt="Calculate duties" style={{ width: 120, height: 120, opacity: 0.6 }} />
                <Box>
                  <Typography sx={{ fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary, mb: 0.5 }}>
                    Calculate Duties
                  </Typography>
                  <Typography sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.sm2, lineHeight: 1.5 }}>
                    Enter your HTS code and country of origin to instantly calculate applicable duty rates, tariffs, and estimated costs.
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Loading state */}
          {isFetching && !data && (
            <Stack spacing={1.5}>
              <Skeleton variant="text" width={160} height={14} />
              <Skeleton variant="text" width={100} height={36} />
              <Skeleton variant="text" width={240} height={12} />
              <Divider sx={{ my: 1.5 }} />
              <Skeleton variant="rectangular" height={44} />
              <Skeleton variant="rectangular" height={44} />
            </Stack>
          )}

          {data && (
            <Stack spacing={0}>
              {/* Rate header */}
              <Box>
                <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, textTransform: 'uppercase', color: tokens.text.muted, letterSpacing: '0.05em', mb: 0.25 }}>
                  Total ad valorem rate
                </Typography>
                {isFetching ? (
                  <Skeleton variant="text" width={100} height={36} />
                ) : (
                  <Typography sx={{ fontSize: '1.75rem', fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary, lineHeight: 1.1 }}>
                    {totalRatePct}%
                  </Typography>
                )}

                {/* Matched code */}
                {(matchedLabel || data?.htsDescription) && (
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, mt: 0.5 }}>
                    {matchedLabel}{matchedLabel && data?.htsDescription ? ' — ' : ''}{data?.htsDescription}
                  </Typography>
                )}

                {data?.secondaryDutyRateString && data.secondaryDutyRateString.toString().trim().toLowerCase() !== 'null' && (
                  <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary, mt: 0.25 }}>
                    Specific Duty (HTS): {data.secondaryDutyRateString}
                  </Typography>
                )}

                {/* FTA alert */}
                {data?.ftaApplicable && (
                  <Alert severity="success" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: tokens.fontSize.xs } }}>
                    Preferential origin applies{data?.ftaProgramName ? ` under ${data.ftaProgramName}` : ''}. General duty may be exempt.
                  </Alert>
                )}
              </Box>

              {/* Goods Value section */}
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, textTransform: 'uppercase', color: tokens.text.muted, letterSpacing: '0.05em', mb: 0.75 }}>
                  Goods Value
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    value={goodsValue}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const cleaned = raw.replace(/[^0-9.]/g, '');
                      setGoodsValue(cleaned);
                    }}
                    placeholder="10,000.00"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      inputMode: 'decimal',
                    }}
                    sx={{ width: 160, '& .MuiInputBase-input': { fontSize: tokens.fontSize.sm2 } }}
                  />
                  {[{ label: '$1k', value: '1000' }, { label: '$10k', value: '10000' }, { label: '$100k', value: '100000' }].map((chip) => (
                    <Chip
                      key={chip.value}
                      label={chip.label}
                      size="small"
                      onClick={() => setGoodsValue(chip.value)}
                      sx={{
                        fontSize: tokens.fontSize["2xs"],
                        fontWeight: tokens.fontWeight.bold,
                        bgcolor: tokens.bg.muted,
                        color: tokens.text.secondary,
                        height: 20,
                        cursor: 'pointer',
                        '& .MuiChip-label': { px: 0.75 },
                        '&:hover': { bgcolor: tokens.bg.border },
                      }}
                    />
                  ))}
                  <Box sx={{ ml: 'auto', textAlign: 'right' }}>
                    <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, textTransform: 'uppercase', color: tokens.text.muted, letterSpacing: '0.05em' }}>
                      Est. Duty
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary }}>
                      ${estimatedDutyAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider sx={{ my: 2, borderColor: tokens.border.default }} />

              {/* Breakdown lines — each as expandable card with its own rules */}
              {isFetching ? (
                <Stack spacing={1}>
                  <Skeleton variant="rectangular" height={48} />
                  <Skeleton variant="rectangular" height={48} />
                </Stack>
              ) : (
                <Stack spacing={1}>
                  {data.breakdownLines.map((line: any, idx: number) => {
                    const colors = getLineColor(line.lineType);
                    const rules = line.applicableRules || [];
                    const exclusions = line.exclusionsApplied || [];
                    return (
                      <Accordion
                        key={idx}
                        defaultExpanded
                        disableGutters
                        elevation={0}
                        sx={{
                          border: `1px solid ${tokens.border.default}`,
                          borderRadius: `${tokens.radius.md} !important`,
                          '&:before': { display: 'none' },
                          overflow: 'hidden',
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore sx={{ fontSize: 16, color: tokens.text.muted }} />}
                          sx={{
                            minHeight: 36,
                            px: 1.5,
                            '& .MuiAccordionSummary-content': { my: 0.5 },
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ flex: 1, mr: 1 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Chip
                                label={getLineLabel(line.lineType)}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: tokens.fontSize["2xs"],
                                  fontWeight: tokens.fontWeight.bold,
                                  bgcolor: colors.bg,
                                  color: colors.text,
                                  borderRadius: '4px',
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                              {line.portion < 1 && (
                                <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted }}>
                                  ({(line.portion * 100).toFixed(0)}% of value)
                                </Typography>
                              )}
                              {exclusions.length > 0 && (
                                <StarIcon sx={{ fontSize: 12, color: getAuthorityStyle(exclusions[0]?.authority).color }} />
                              )}
                            </Stack>
                            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary }}>
                              {(line.totalRate * 100).toFixed(2)}%
                            </Typography>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
                          {rules.length > 0 ? (
                            <Stack spacing={0.5}>
                              <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, textTransform: 'uppercase', color: tokens.text.muted, letterSpacing: '0.05em', mb: 0.25 }}>
                                Applied Rules
                              </Typography>
                              {rules.map((rule: any, ridx: number) => {
                                if (rule.columnId === 'secondary-duty') {
                                  const s = (data?.secondaryDutyRateString || '').toString().trim();
                                  if (!s || s.toLowerCase() === 'null') return null;
                                  return (
                                    <Stack key={ridx} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.25 }}>
                                      <Stack direction="row" spacing={0.75} alignItems="center">
                                        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: tokens.text.secondary, flexShrink: 0 }} />
                                        <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body }}>
                                          Specific Duty: {s}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                  );
                                }

                                const excluded = exclusions.some((e: any) => e.columnId === rule.columnId);
                                const exclusionColor = excluded
                                  ? getAuthorityStyle(exclusions.find((e: any) => e.columnId === rule.columnId)?.authority).color
                                  : undefined;
                                const col = columnMap.get(rule.columnId);
                                const hasLink = col && Array.isArray((col as any).citations) && (col as any).citations.length > 0;
                                const firstUrl = hasLink ? (col as any).citations[0]?.url : null;

                                return (
                                  <Stack key={ridx} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.25 }}>
                                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                                      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: colors.text, flexShrink: 0 }} />
                                      <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {rule.columnName}
                                      </Typography>
                                      {excluded && (
                                        <>
                                          <StarIcon sx={{ fontSize: 11, color: exclusionColor, flexShrink: 0 }} />
                                          <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.status.success, fontWeight: tokens.fontWeight.semibold }}>→ 0%</Typography>
                                        </>
                                      )}
                                    </Stack>
                                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
                                      <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
                                        {(rule.rate * 100).toFixed(2)}%
                                      </Typography>
                                      {firstUrl && (
                                        <a href={firstUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }}>
                                          <LinkIcon sx={{ fontSize: 12, color: tokens.text.muted, '&:hover': { color: tokens.text.secondary } }} />
                                        </a>
                                      )}
                                    </Stack>
                                  </Stack>
                                );
                              })}
                              {/* Exclusion details within this line */}
                              {exclusions.length > 0 && (
                                <Box sx={{ mt: 0.5, pt: 0.5, borderTop: `1px solid ${tokens.border.light}` }}>
                                  {exclusions.map((e: any, eidx: number) => (
                                    <Stack key={eidx} direction="row" spacing={0.5} alignItems="center" sx={{ py: 0.25 }}>
                                      <StarIcon sx={{ fontSize: 11, color: getAuthorityStyle(e.authority).color }} />
                                      <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.secondary }}>
                                        {e.listName}: {e.columnName} — {e.matchedCode}
                                      </Typography>
                                    </Stack>
                                  ))}
                                </Box>
                              )}
                            </Stack>
                          ) : (
                            <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, fontStyle: 'italic' }}>
                              No applicable duties on this portion.
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Stack>
              )}

              {/* Metal content bar */}
              {showMetalPercentage && metalPercentage && (
                <Box sx={{ mt: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, color: tokens.text.secondary }}>Metal Content</Typography>
                    <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>{metalPercentage}%</Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(Number(metalPercentage) || 0, 100)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: tokens.bg.border,
                      '& .MuiLinearProgress-bar': { bgcolor: tokens.text.body, borderRadius: 3 },
                    }}
                  />
                </Box>
              )}

              {/* Sources */}
              {footnoteColumns.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.bold, textTransform: 'uppercase', color: tokens.text.muted, letterSpacing: '0.05em', mb: 0.75 }}>
                    Sources
                  </Typography>
                  <Stack spacing={0.75}>
                    {footnoteColumns.map((dc: any, idx: number) => (
                      <Box key={dc._id}>
                        <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
                          {idx + 1}. {dc.name}
                        </Typography>
                        <Stack spacing={0.25} sx={{ pl: 1.5, mt: 0.25 }}>
                          {(dc.citations || []).map((c: any, cidx: number) => (
                            <a
                              key={cidx}
                              href={c.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: tokens.brand.primary, textDecoration: 'none', fontSize: tokens.fontSize.xs, wordBreak: 'break-all', display: 'flex', alignItems: 'center', gap: 3 }}
                            >
                              {c.url}
                              <LinkIcon sx={{ fontSize: 10, color: tokens.text.muted }} />
                            </a>
                          ))}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      {/* Create Product Modal */}
      <CreateProductForm
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onProductCreated={() => {
          setIsCreateOpen(false);
          window.dispatchEvent(new CustomEvent('td:navigate-products'));
        }}
        initialData={{
          htsCode: debouncedHtsCode,
          countryOfOrigin: debouncedCountryOfOrigin,
          metalPercentage: showMetalPercentage && metalPercentage ? Number(metalPercentage) : undefined,
        }}
      />
    </Box>
  );
};

export default DutyCalculator;
